/* @jsxImportSource @emotion/react */

import React from 'react'
import { keyframes } from '@emotion/react'

import { mediaQueryMedium, mediaQuerySmall } from '../../components/style'
import { useRoutes } from '../../routes'
import { CustomerStoryCard } from './CustomerStoryCard'
import type { CustomerStoryCardData } from './customerStoryCardFields'

const gridCss = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 24,
  [mediaQueryMedium]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  [mediaQuerySmall]: { gridTemplateColumns: 'minmax(0, 1fr)' },
} as const

// Only the three-column grid has room for a double-width card.
const wideCardCss = {
  gridColumn: 'span 2',
  [mediaQueryMedium]: { gridColumn: 'auto' },
} as const

const cardEnter = keyframes({
  from: { opacity: 0, transform: 'translateY(16px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
})

const REVEAL_STAGGER_MS = 40
const REVEAL_MAX_DELAY_MS = 480

// Same cascade as the learn index rows. `backwards` holds the start frame only
// through the delay, so the card's own hover transform still applies after.
function revealCss(index: number) {
  return {
    animation: `${cardEnter} 420ms cubic-bezier(0.22, 1, 0.36, 1) backwards`,
    animationDelay: `${Math.min(index * REVEAL_STAGGER_MS, REVEAL_MAX_DELAY_MS)}ms`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  }
}

// On three columns the rows repeat every ten cards: wide + single, three
// singles, single + wide, three singles — so the wide card swaps sides.
const CARDS_PER_CYCLE = 10
const WIDE_CARD_POSITIONS = new Set([0, 6])

function isWideCard(index: number) {
  return WIDE_CARD_POSITIONS.has(index % CARDS_PER_CYCLE)
}

export function CustomerStoryGrid(props: {
  caseStudies: CustomerStoryCardData[]
  withWideCards: boolean
  animateReveal: boolean
}) {
  const routes = useRoutes()

  return (
    <div css={gridCss}>
      {props.caseStudies.map((caseStudy, index) => (
        <CustomerStoryCard
          key={caseStudy._id}
          caseStudy={caseStudy}
          href={routes.customers.detail({ slug: caseStudy.slug.current })}
          css={[
            props.withWideCards && isWideCard(index) && wideCardCss,
            props.animateReveal && revealCss(index),
          ]}
        />
      ))}
    </div>
  )
}
